// Baselayer Results Explorer — reusable circular SVG rating gauge. Shows a
// letter grade (A–F) in the centre with a risk label below, the arc tinted by
// the derived risk band. Pure SVG + design tokens (no chart lib, no hardcoded
// colours — fills use currentColor driven by a token text class).

export type RiskBand = "Low" | "Moderate" | "High" | "Unknown";

/** Maps a letter grade to its risk band: A/B = Low, C = Moderate, D/F = High. */
export function ratingToRisk(rating: string | null | undefined): RiskBand {
  const letter = (rating ?? "").trim().charAt(0).toUpperCase();
  switch (letter) {
    case "A":
    case "B":
      return "Low";
    case "C":
      return "Moderate";
    case "D":
    case "F":
      return "High";
    default:
      return "Unknown";
  }
}

const RISK_TONE: Record<RiskBand, string> = {
  Low: "text-status-success",
  Moderate: "text-status-warning",
  High: "text-status-error",
  Unknown: "text-gray-mid",
};

/** Fraction of the arc filled, by band (Low fullest, Unknown empty-ish). */
const RISK_FILL: Record<RiskBand, number> = {
  Low: 0.92,
  Moderate: 0.6,
  High: 0.28,
  Unknown: 0,
};

interface RatingGaugeProps {
  /** Section label above the gauge, e.g. "KYB" or "Risk". */
  label: string;
  /** Letter grade A–F; null renders a muted "—" gauge. */
  rating: string | null;
  className?: string;
}

const SIZE = 96;
const STROKE = 8;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function RatingGauge({ label, rating, className }: RatingGaugeProps) {
  const risk = ratingToRisk(rating);
  const tone = RISK_TONE[risk];
  const fraction = RISK_FILL[risk];
  const dash = CIRCUMFERENCE * fraction;

  return (
    <div className={`flex flex-col items-center gap-space-1 ${className ?? ""}`}>
      <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
        {label}
      </span>
      <div className={`relative ${tone}`} style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label={`${label} grade ${rating ?? "unknown"}, ${risk} risk`}
        >
          {/* Track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.15}
            strokeWidth={STROKE}
          />
          {/* Value arc */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display text-h2 font-bold text-onyx">
          {rating ?? "—"}
        </span>
      </div>
      <span className={`text-small font-medium ${tone}`}>{risk} risk</span>
    </div>
  );
}
